import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Store } from '@ngrx/store';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { DocumentTypesUppercaseEnum } from 'common/src/modules/modals/modals-common/link-document-modal/enums/ducument-types.enum';
import { FileAttachmentModel, ResponseModel } from '../../models';
import { DisplayToaster } from '../../decorators/toaster';
import { environment } from '../../../../environments/environment';
import { selectCompanyProfile } from '../../../administration/store/selectors';
import { CompanyProfile } from '../../../administration/models/company-profile.model';
import { AppState } from '../../../store/state/app.state';

@Injectable()
export class FilesAttachmentApiService {

  private readonly apiEndpoint: string = `${environment.javaApiVersion}/workspaces`;
  private wid: number;

  private accountingApiUrl(url: string = ''): string {
    return this.apiEndpoint + `/${this.wid}/attachments/` + url;
  }

  constructor(
    private http: HttpClient,
    private toasterService: ToasterService,
    private readonly store: Store<AppState>
  ) {
    this.store.select(selectCompanyProfile)
      .subscribe((companyProfile: CompanyProfile) => this.wid = companyProfile.workspaceId);
  }

  private baseApiUrl(documentType: DocumentTypesUppercaseEnum): string {
    switch (documentType) {
      case DocumentTypesUppercaseEnum.PRODUCT:
        return 'products';
      case DocumentTypesUppercaseEnum.PARTNER:
        return 'partners';
      case DocumentTypesUppercaseEnum.OFR:
        return 'trade-offers';
      case DocumentTypesUppercaseEnum.SO:
        return 'sales-orders';
      case DocumentTypesUppercaseEnum.PO:
        return 'purchase-orders';
      case DocumentTypesUppercaseEnum.SBC:
        return 'subscriptions';
      case DocumentTypesUppercaseEnum.DN:
        return 'delivery-notes';
    }

  }

  public getAttachments(documentType: DocumentTypesUppercaseEnum, documentId: number|string): Observable<FileAttachmentModel[]> {
    return this.http.get<ResponseModel<FileAttachmentModel[]>>(`/${this.baseApiUrl(documentType)}/${documentId}/file`)
      .pipe(
        map((response: ResponseModel<FileAttachmentModel[]>) => response.data)
      );
  }

  public getAccountingAttachments(documentType: DocumentTypesUppercaseEnum, documentId: number|string): Observable<FileAttachmentModel[]> {
    return this.http.get<ResponseModel<FileAttachmentModel[]>>(`${this.accountingApiUrl(documentType)}/${documentId}`)
      .pipe(
        map((response: ResponseModel<FileAttachmentModel[]>) => response.data)
      );
  }

  public uploadAttachment(documentType: DocumentTypesUppercaseEnum, documentId: number|string, file: File): Observable<any> {
    const formData = new FormData();
    formData.set('file', file);

    return this.http.post<ResponseModel<any>>(`/${this.baseApiUrl(documentType)}/${documentId}/file`, formData)
      .pipe(
        map((response: ResponseModel<any>) => response.data),
        catchError((error) => {
          if (error.status === 413) {
            this.toasterService.notify({type: 'error', message: 'ATTACHMENTS.FILE_SIZE_ERROR'});
            return throwError(error);
          }
          this.toasterService.notify({type: 'error', message: error.error.message});
          return throwError(error);
        }),
      );
  }

  public uploadAccountingAttachment(documentType: DocumentTypesUppercaseEnum, documentId: number|string, file: File): Observable<any> {
    const formData = new FormData();
    formData.set('file', file);

    return this.http.post<ResponseModel<any>>(`${this.accountingApiUrl(documentType)}/${documentId}`, formData)
      .pipe(
        map((response: ResponseModel<any>) => response.data),
        catchError((error) => {
          if (error.status === 413) {
            this.toasterService.notify({type: 'error', message: 'ATTACHMENTS.FILE_SIZE_ERROR'});
            return throwError(error);
          }
          this.toasterService.notify({type: 'error', message: error.error.message || error.error.errors});
          return throwError(error);
        }),
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteAttachment(documentType: DocumentTypesUppercaseEnum, documentId: number|string, fileId: number): Observable<any> {
    return this.http.delete<ResponseModel<any>>(`/${this.baseApiUrl(documentType)}/${documentId}/file/${fileId}`);
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteAccountingAttachment(documentType: DocumentTypesUppercaseEnum, documentId: number|string, fileId: number): Observable<any> {
    return this.http.delete<ResponseModel<any>>(`${this.accountingApiUrl(documentType)}/${documentId}/${fileId}`);
  }

  public getDownloadAttachment(fileId: number): Observable<any> {
    return this.http.get<ResponseModel<any>>(`/file/${fileId}/attachment`)
      .pipe(
        map((response: ResponseModel<any>) => response.data),
      );
  }

  public getDownloadAccountingAttachment(documentType: DocumentTypesUppercaseEnum, documentId: number|string, fileId: number): Observable<any> {
    return this.http.get<ResponseModel<any>>(`${this.accountingApiUrl(documentType)}/${documentId}/${fileId}`)
      .pipe(
        map((response: ResponseModel<any>) => response.data),
      );
  }

}
